import api from '@pray/shared/utils/api';

import { mapRerenderClip, mapReelsPage, mapReelsTask, mapStartReels, toCaptionCuesPayload } from './mappers';
import { toCaptionStylePayload } from './style';
import type {
  CaptionCue,
  CaptionStyle,
  ReelsPageResponse,
  RerenderClipResponse,
  StartReelsResponse,
  TaskResponse,
} from './types';

const artistUrl = (artistId: string) => `/web/studio/artists/${artistId}`;

const requireId = (value: string | undefined, name: string) => {
  if (!value) throw Error(`${name} is required`);
};

/** GET …/contents/:contentId/reels: availability, eligibility, price, the latest click, suggested tags, capabilities. */
export async function getReelsPage({ artistId, contentId }: { artistId: string; contentId: string }) {
  requireId(artistId, 'Artist ID');
  requireId(contentId, 'Content ID');
  const response = await api.executeGetRequest<{ data: ReelsPageResponse }>(
    `${artistUrl(artistId)}/contents/${contentId}/reels`
  );
  return mapReelsPage(response.data.data);
}

/**
 * POST …/contents/:contentId/reels, one Generate click: 202 at once with every task `generating`. The same
 * `idempotencyKey` again answers the same tasks with `replayed: true` and charges nothing.
 */
export async function startReels({
  artistId,
  contentId,
  creditConfirmationId = null,
  idempotencyKey,
  tags = [],
}: {
  artistId: string;
  contentId: string;
  /** From the credit confirmation for operation `reels`: the price the leader saw on the button. */
  creditConfirmationId?: string | null;
  /** One per click attempt */
  idempotencyKey: string;
  tags?: string[];
}) {
  requireId(artistId, 'Artist ID');
  requireId(contentId, 'Content ID');
  const response = await api.executePostRequest<{ data: StartReelsResponse }>(
    `${artistUrl(artistId)}/contents/${contentId}/reels`,
    { ...(creditConfirmationId ? { creditConfirmationId } : {}), idempotencyKey, tags }
  );
  return mapStartReels(response.data.data);
}

/** GET …/reels/tasks/:taskId */
export async function getReelsTask({ artistId, taskId }: { artistId: string; taskId: string }) {
  requireId(artistId, 'Artist ID');
  requireId(taskId, 'Task ID');
  const response = await api.executeGetRequest<{ data: TaskResponse }>(`${artistUrl(artistId)}/reels/tasks/${taskId}`);
  return mapReelsTask(response.data.data);
}

/**
 * POST …/clips/:clipId/rerender with edited captions and caption style. Free by default (`rerender.credits` 0): no
 * `creditConfirmationId`. When priced, the confirmation for operation `reel_rerender` comes with it.
 */
export async function rerenderClip({
  artistId,
  clipId,
  idempotencyKey,
  creditConfirmationId = null,
  captions,
  style,
}: {
  artistId: string;
  clipId: string;
  idempotencyKey: string;
  creditConfirmationId?: string | null;
  captions: CaptionCue[];
  style: CaptionStyle;
}) {
  requireId(artistId, 'Artist ID');
  requireId(clipId, 'Clip ID');
  const response = await api.executePostRequest<{ data: RerenderClipResponse }>(
    `${artistUrl(artistId)}/clips/${clipId}/rerender`,
    {
      ...(creditConfirmationId ? { creditConfirmationId } : {}),
      idempotencyKey,
      captions: toCaptionCuesPayload(captions),
      captionStyle: toCaptionStylePayload(style),
    }
  );
  return mapRerenderClip(response.data.data);
}
